import { DropdownMultiSelectInput, EyeOutline, StatCard } from '@aslanpay/portal-common-lib';
import { Box, Flex, Text } from '@chakra-ui/react';
import AuthenticatedPageWrapper from 'app/components/UI/AuthenticatedPageWrapper';
import CenterSpinner from 'app/components/UI/CenterSpinner';
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import RecognitionApprovalsList from './RecognitionApprovalsList/RecognitionApprovalsList';
import { ApprovalsTab, ESCALATION_DAYS_THRESHOLD } from './Approvals.types';
import useApprovals from './useApprovals';

const APPROVALS_TABS: ApprovalsTab[] = ['escalations', 'pendingApprovals'];
const DEFAULT_TAB: ApprovalsTab = 'escalations';

function isValidApprovalsTab(tab: string | null): tab is ApprovalsTab {
  return APPROVALS_TABS.includes(tab as ApprovalsTab);
}

function Approvals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: ApprovalsTab = isValidApprovalsTab(rawTab) ? rawTab : DEFAULT_TAB;

  useEffect(() => {
    if (!isValidApprovalsTab(rawTab)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', DEFAULT_TAB);
          return next;
        },
        { replace: true }
      );
    }
  }, [rawTab, setSearchParams]);
  const setActiveTab = useCallback(
    (tab: ApprovalsTab) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        next.delete('selectedPots');
        return next;
      });
    },
    [setSearchParams]
  );

  const {
    isLoading,
    pendingApprovals,
    escalations,
    selectedPotIds,
    setSelectedPotIds,
    potOptions,
  } = useApprovals(activeTab);

  const renderTabs = useMemo(() => {
    const tabCards: {
      title: string;
      description: string;
      tab: ApprovalsTab;
      subTitle?: string;
      value: number;
    }[] = [
      {
        title: 'Escalations',
        subTitle: `(waiting ${ESCALATION_DAYS_THRESHOLD}+ days)`,
        description:
          activeTab === 'escalations' ? 'All escalations shown below' : 'Click to see escalations',
        tab: 'escalations',
        value: escalations.length,
      },
      {
        title: 'Pending approvals',
        description:
          activeTab === 'pendingApprovals'
            ? 'All pending approvals shown below'
            : 'Click to see approvals for all pots',
        tab: 'pendingApprovals',
        value: pendingApprovals.length,
      },
    ];

    return (
      <Flex direction="row" gap="18px">
        {tabCards.map(({ title, subTitle, tab, ...props }) => (
          <StatCard
            {...props}
            key={tab}
            isActive={activeTab === tab}
            onClick={() => setActiveTab(tab as ApprovalsTab)}
            title={
              <>
                {title}
                {subTitle && (
                  <Text
                    as="span"
                    color={activeTab === tab ? 'brand.mediumPurple' : 'brand.mediumGray2'}
                  >
                    {' '}
                    {subTitle}
                  </Text>
                )}
              </>
            }
            icon={activeTab !== tab && <EyeOutline boxSize="24px" />}
          />
        ))}
      </Flex>
    );
  }, [activeTab, escalations.length, pendingApprovals.length, setActiveTab]);

  const renderPotDropdown = useMemo(() => {
    return (
      <Box w="366px">
        <DropdownMultiSelectInput
          placeholder="Filter by pot"
          selected={selectedPotIds}
          options={potOptions}
          onChange={setSelectedPotIds}
          isSearchable
          isSelectAllEnabled
          selectAllDisplayLabel="All pots"
          renderCustomDisplayLabel={(selected, options) => {
            if (selected.length === 0) return '';
            if (selected.length === options.length) return 'All pots';
            const firstLabel = options.find((o) => o.value === selected[0])?.text ?? '';
            if (selected.length === 1) return firstLabel;
            return `${firstLabel} +${selected.length - 1}`;
          }}
        />
      </Box>
    );
  }, [selectedPotIds, potOptions, setSelectedPotIds]);

  const renderContent = () => {
    if (isLoading) {
      return <CenterSpinner />;
    }

    return (
      <RecognitionApprovalsList
        tab={activeTab}
        list={activeTab === 'escalations' ? escalations : pendingApprovals}
        potDropdown={renderPotDropdown}
      />
    );
  };

  return (
    <AuthenticatedPageWrapper title="Approvals">
      <Flex flexDir="column" justifyContent="center" gap="30px" h="full">
        <Flex flexDir="column" gap="8px">
          <Text as="h2" textStyle="h2" color="brand.darkPurple">
            Approvals
          </Text>
          <Text color="brand.darkPurple">Review approvals requiring your action</Text>
        </Flex>

        {renderTabs}

        {renderContent()}
      </Flex>
    </AuthenticatedPageWrapper>
  );
}

export default Approvals;
